import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { createContext, memo, useContext, useEffect } from 'react';
import { IoIosClose } from 'react-icons/io';
import tw from 'twin.macro';

const ModalDragHandleContext = createContext(null);

// Lets content passed via `content` start the sheet drag from a specific
// handle element (e.g. a grab bar) instead of the whole card being draggable.
export const useModalDragHandle = () => useContext(ModalDragHandleContext);

const CLOSE_DRAG_OFFSET = 120;
const CLOSE_DRAG_VELOCITY = 500;

const Modal = ({
  content,
  title,
  handleClose,
  open,
  containerModalStyle,
  titleStyle,
  hideIcon = false,
  backgroundModal,
  modalStyle,
  icon,
  dragToClose = false,
}) => {
  const dragControls = useDragControls();

  useEffect(() => {
    // Disable scroll on body when modal is open
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Clean up to enable scroll again when the component is unmounted or modal is closed
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleDragEnd = (event, info) => {
    if (info.offset.y > CLOSE_DRAG_OFFSET || info.velocity.y > CLOSE_DRAG_VELOCITY) {
      handleClose?.();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        onClick={handleClose}
        style={{ background: backgroundModal, ...modalStyle }}
        tw="bg-[#434E1280] backdrop-blur-[5px] w-full min-h-screen min-h-dvh min-h-svh overflow-y-auto fixed top-0 items-center left-0 [z-index: 9999] flex justify-center flex-col"
        css={dragToClose && tw`max-lg:justify-end!`}
        initial={{ opacity: 0 }}
        animate={open ? { opacity: 1 } : { opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={open ? { opacity: 1 } : { opacity: 0 }}
          onClick={e => {
            e.stopPropagation();
          }}
          tw="bg-white md:rounded-primary-radius rounded-[20px] relative md:min-w-[400px] min-w-[300px] h-[80vh] overflow-y-auto"
          css={containerModalStyle}
          drag={dragToClose ? 'y' : false}
          dragListener={false}
          dragControls={dragToClose ? dragControls : undefined}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.5 }}
          dragSnapToOrigin
          onDragEnd={dragToClose ? handleDragEnd : undefined}
        >
          {icon && icon}
          {!hideIcon && (
            <div tw="absolute [inset-inline-end: 25px] top-6 bg-white rounded-full">
              <IoIosClose size={30} onClick={handleClose} tw="text-gray-text cursor-pointer" />
            </div>
          )}
          {title && (
            <p css={[titleStyle, tw`pb-2 text-lg font-bold`]} tw="">
              {title}
            </p>
          )}
          <ModalDragHandleContext.Provider value={dragToClose ? dragControls : null}>
            {content}
          </ModalDragHandleContext.Provider>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
export default memo(Modal);
